import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { styles, inputStyle, labelStyle } from "../shared/styles";
import { LuPlus, LuX, LuChevronDown, LuPencil, LuTrash2 } from "react-icons/lu";
import { StatusSelect } from "../shared/StatusSelect";

// A target's three states. Reuses the app's shared status control rather than
// being a fourth way of showing a small set of choices.
const TARGET_OPTIONS = ["in_progress", "done", "incomplete"];
const TARGET_CFG = {
  in_progress: { label: "In progress", bg: "#fff7ed", color: "#c2410c" },
  done:        { label: "Done",        bg: "#dcfce7", color: "#15803d" },
  incomplete:  { label: "Incomplete",  bg: "#f1f5f9", color: "#64748b" },
};

const STATUS_COLORS = {
  incomplete: { bg: "#fff7ed", color: "#c2410c" },
  complete:   { bg: "#dcfce7", color: "#15803d" },
};

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.incomplete;
  return (
    <span style={{ fontSize: "11px", fontWeight: "700", padding: "3px 10px", borderRadius: "20px", backgroundColor: c.bg, color: c.color, whiteSpace: "nowrap" }}>
      {status === "complete" ? "Complete" : "Incomplete"}
    </span>
  );
}

function formatDate(dt) {
  return new Date(dt).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}

export function ServiceRequestsPage() {
  const userRole = localStorage.getItem("sfp_role");
  const canEditDelete = userRole === "admin" || userRole === "management";

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);

  const [filterStatus, setFilterStatus] = useState("all");
  const [hoveredRow, setHoveredRow] = useState(null);

  const [selected, setSelected] = useState(null);
  const [editStatus, setEditStatus] = useState("");
  const [editAssignedTo, setEditAssignedTo] = useState("");
  const [editScheduledDate, setEditScheduledDate] = useState("");
  const [editScheduledTime, setEditScheduledTime] = useState("");
  const [editFlexible, setEditFlexible] = useState(false);
  const [editActionsTaken, setEditActionsTaken] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [siteSearch, setSiteSearch] = useState("");
  const [siteResults, setSiteResults] = useState([]);
  const [createSite, setCreateSite] = useState(null);
  const [createTargets, setCreateTargets] = useState([""]);
  const [createAssignedTo, setCreateAssignedTo] = useState("");
  const [createContactName, setCreateContactName] = useState("");
  const [createContactPhone, setCreateContactPhone] = useState("");
  const [createContactEmail, setCreateContactEmail] = useState("");
  const [createScheduledDate, setCreateScheduledDate] = useState("");
  const [createScheduledTime, setCreateScheduledTime] = useState("");
  const [createFlexible, setCreateFlexible] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadRequests();
    axios.get(`${API}/sites/`).then(r => setSites(r.data)).catch(() => {});
    axios.get(`${API}/technicians/`).then(r => setUsers(r.data)).catch(() => {});
  }, []);

  const loadRequests = () => {
    setLoading(true);
    axios.get(`${API}/service-requests/`)
      .then(r => { setRequests(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    if (siteSearch.length < 2) { setSiteResults([]); return; }
    const q = siteSearch.toLowerCase();
    setSiteResults(sites.filter(s =>
      s.site_name.toLowerCase().includes(q) || s.site_address.toLowerCase().includes(q)
    ).slice(0, 8));
  }, [siteSearch, sites]);

  const openSelected = (req) => {
    setSelected(req);
    setEditStatus(req.status);
    setEditAssignedTo(req.assigned_to ? String(req.assigned_to) : "");
    setEditScheduledDate(req.scheduled_date || "");
    setEditScheduledTime(req.scheduled_time?.slice(0, 5) || "");
    setEditFlexible(req.flexible || false);
    setEditActionsTaken(req.actions_taken || "");
    setDeleteConfirm(false);
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/service-requests/${selected.id}/`, {
        status: editStatus,
        assigned_to: editAssignedTo || null,
        scheduled_date: editScheduledDate || null,
        scheduled_time: editScheduledTime || null,
        flexible: editFlexible,
        actions_taken: editActionsTaken,
      });
      setSelected(res.data);
      loadRequests();
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!selected) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/service-requests/${selected.id}/`);
      setSelected(null);
      loadRequests();
    } catch (err) { console.error(err); }
    setDeleting(false);
  };

  // The office can correct a target from the detail panel. Targets are their
  // own records, so this patches one rather than resubmitting the request.
  const setTargetStatus = async (targetId, next) => {
    try {
      await axios.patch(`${API}/service-request-targets/${targetId}/`, {
        status: next,
      });
      loadRequests();
    } catch (err) {
      console.error("Failed to set target status", err);
    }
  };

  const resetCreate = () => {
    setSiteSearch(""); setSiteResults([]); setCreateSite(null);
    setCreateTargets([""]);
    setCreateAssignedTo(""); setCreateContactName("");
    setCreateContactPhone(""); setCreateContactEmail("");
    setCreateScheduledDate(""); setCreateScheduledTime("");
    setCreateFlexible(false);
    setShowContact(false);
  };

  const handleCreate = async () => {
    const lines = createTargets.map((t) => t.trim()).filter(Boolean);
    if (!createSite || lines.length === 0) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/service-requests/`, {
        site: createSite.id,
        assigned_to: createAssignedTo || null,
        request_contact_name: createContactName,
        request_contact_phone: createContactPhone,
        request_contact_email: createContactEmail,
        // Schedule it here rather than making the office come back through the
        // edit panel — an unscheduled request is one the tech never sees.
        scheduled_date: createScheduledDate || null,
        scheduled_time: createScheduledTime || null,
        flexible: createFlexible,
      });
      // Targets are their own records, so they are posted after the request
      // exists. Sequential rather than parallel to keep their order stable.
      for (let i = 0; i < lines.length; i++) {
        await axios.post(`${API}/service-request-targets/`, {
          request: res.data.id,
          description: lines[i],
          order: i,
        });
      }
      setShowCreate(false);
      resetCreate();
      loadRequests();
    } catch (err) { console.error(err); }
    setCreating(false);
  };

  const pill = (active, onClick, label) => (
    <button onClick={onClick} style={{
      padding: "6px 14px", borderRadius: "20px", cursor: "pointer", whiteSpace: "nowrap",
      border: `1px solid ${active ? colors.darkGrey : colors.border}`,
      backgroundColor: active ? colors.darkGrey : "white",
      color: active ? "white" : colors.slate,
      fontSize: "12px", fontWeight: "600",
    }}>
      {label}
    </button>
  );

  const userName = (u) => (u.first_name && u.last_name) ? `${u.first_name} ${u.last_name}` : u.username;

  const filtered = requests.filter(r => {
    if (filterStatus !== "all" && r.status !== filterStatus) return false;
    return true;
  });

  const cols = `2fr 3fr 110px 140px 90px${canEditDelete ? " 64px" : ""}`;

  const iconBtnStyle = { background: "none", border: "none", cursor: "pointer", padding: "4px", borderRadius: "4px", color: colors.slate, display: "flex", alignItems: "center" };

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      {/* Main list */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={styles.topbar}>
          <div style={styles.pageTitle}>Service Requests</div>
          <button onClick={() => { resetCreate(); setShowCreate(true); }} style={styles.addButton}>
            <LuPlus size={15} /> New Request
          </button>
        </div>

        {/* Filters */}
        <div style={{ padding: "10px 24px", backgroundColor: colors.white, borderBottom: `1px solid ${colors.border}`, display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: "11px", fontWeight: "700", color: colors.slate, textTransform: "uppercase", letterSpacing: "0.5px" }}>Status:</span>
          {pill(filterStatus === "all", () => setFilterStatus("all"), "All")}
          {pill(filterStatus === "incomplete", () => setFilterStatus("incomplete"), "Incomplete")}
          {pill(filterStatus === "complete", () => setFilterStatus("complete"), "Complete")}
        </div>

        <div style={{ flex: 1, overflowY: "auto" }}>
          <div style={styles.card}>
            {loading ? (
              <div style={styles.loading}>Loading...</div>
            ) : filtered.length === 0 ? (
              <div style={styles.loading}>No service requests found.</div>
            ) : (
              <>
                <div style={{ ...styles.tableHeader, gridTemplateColumns: cols }}>
                  <div>Site</div><div>Targets</div>
                  <div>Status</div><div>Assigned To</div><div>Date</div>
                  {canEditDelete && <div></div>}
                </div>
                {filtered.map(req => (
                  <div
                    key={req.id}
                    onClick={() => openSelected(req)}
                    onMouseEnter={() => setHoveredRow(req.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    style={{
                      display: "grid", gridTemplateColumns: cols,
                      padding: "12px 20px", borderBottom: `1px solid ${colors.border}`,
                      alignItems: "center", cursor: "pointer", transition: "background 0.1s",
                      backgroundColor: selected?.id === req.id ? "#f0f4ff" : hoveredRow === req.id ? "#f8fafc" : colors.white,
                    }}
                  >
                    <div style={{ fontSize: "14px", fontWeight: "600", color: colors.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{req.site_name}</div>
                    <div style={{ fontSize: "13px", color: colors.slate, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {(req.targets || []).length === 0
                        ? <span style={{ fontStyle: "italic" }}>No targets set</span>
                        : `${req.targets.length - (req.open_target_count ?? 0)}/${req.targets.length} done · ${req.targets[0].description}`}
                    </div>
                    <div><StatusBadge status={req.status} /></div>
                    <div style={{ fontSize: "13px", color: req.assigned_to_name ? colors.text : colors.slate, fontStyle: req.assigned_to_name ? "normal" : "italic" }}>
                      {req.assigned_to_name || "Unassigned"}
                    </div>
                    <div style={{ fontSize: "12px", color: colors.slate }}>{formatDate(req.date_requested)}</div>
                    {canEditDelete && (
                      <div style={{ display: "flex", gap: "2px", opacity: hoveredRow === req.id ? 1 : 0, transition: "opacity 0.15s" }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); openSelected(req); }}
                          title="Edit"
                          style={iconBtnStyle}
                        >
                          <LuPencil size={14} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); openSelected(req); setDeleteConfirm(true); }}
                          title="Delete"
                          style={iconBtnStyle}
                        >
                          <LuTrash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                <div style={styles.count}>{filtered.length} request{filtered.length !== 1 ? "s" : ""}</div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Detail panel */}
      {selected && (
        <>
          <div onClick={() => setSelected(null)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.15)", zIndex: 99 }} />
          <div style={{ position: "fixed", right: 0, top: 0, bottom: 0, width: "440px", backgroundColor: colors.white, boxShadow: "-4px 0 24px rgba(0,0,0,0.12)", zIndex: 100, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ padding: "20px 24px", borderBottom: `1px solid ${colors.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontWeight: "700", fontSize: "16px", color: colors.text }}>Service Request</div>
              <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", cursor: "pointer", color: colors.slate, padding: "4px", lineHeight: 1 }}><LuX size={18} /></button>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", display: "flex", flexDirection: "column", gap: "16px" }}>
              <div>
                <div style={labelStyle}>Site</div>
                <div style={{ fontSize: "14px", fontWeight: "600", color: colors.text }}>{selected.site_name}</div>
                <div style={{ fontSize: "12px", color: colors.slate }}>{selected.account_name}</div>
              </div>
              <div>
                <div style={labelStyle}>Date Requested</div>
                <div style={{ fontSize: "14px", color: colors.text }}>{formatDate(selected.date_requested)}</div>
              </div>
              <div>
                <label style={labelStyle}>Status</label>
                <select value={editStatus} onChange={e => setEditStatus(e.target.value)} style={{ ...inputStyle, width: "100%" }}>
                  <option value="incomplete">Incomplete</option>
                  <option value="complete">Complete</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Assigned To</label>
                <select value={editAssignedTo} onChange={e => setEditAssignedTo(e.target.value)} style={{ ...inputStyle, width: "100%" }}>
                  <option value="">Unassigned</option>
                  {users.map(u => <option key={u.id} value={String(u.id)}>{userName(u)}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Scheduled Date (optional)</label>
                <input
                  type="date"
                  value={editScheduledDate}
                  onChange={e => setEditScheduledDate(e.target.value)}
                  style={{ ...inputStyle, width: "100%" }}
                />
              </div>
              <div>
                <label style={labelStyle}>Scheduled Time (optional)</label>
                <input
                  type="time"
                  value={editScheduledTime}
                  onChange={e => setEditScheduledTime(e.target.value)}
                  style={{ ...inputStyle, width: "100%" }}
                />
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", marginTop: "-4px" }}>
                <input
                  type="checkbox"
                  checked={editFlexible}
                  onChange={e => setEditFlexible(e.target.checked)}
                />
                <span style={{ fontSize: "13px", color: colors.darkGrey }}>Flexible — do anytime</span>
              </label>
              <div>
                <label style={labelStyle}>Targets</label>
                {(selected.targets || []).length === 0 ? (
                  <div style={{ fontSize: "13px", color: colors.amber }}>
                    No targets set — the tech has nothing to work from.
                  </div>
                ) : (
                  selected.targets.map((t, i) => (
                    <div
                      key={t.id}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                        padding: "8px 0",
                        borderBottom: `1px solid ${colors.border}`,
                      }}
                    >
                      <span style={{ fontSize: "13px", fontWeight: "700", color: colors.slate, width: "18px" }}>
                        {i + 1}.
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: "14px", color: colors.text, lineHeight: "1.5", whiteSpace: "pre-wrap" }}>
                          {t.description}
                        </div>
                        {t.notes && (
                          <div style={{ fontSize: "12px", color: colors.slate, marginTop: "3px", fontStyle: "italic" }}>
                            {t.notes}
                          </div>
                        )}
                      </div>
                      <StatusSelect
                        value={t.status}
                        options={TARGET_OPTIONS}
                        cfg={TARGET_CFG}
                        onChange={(next) => setTargetStatus(t.id, next)}
                        title="Set this target's status"
                      />
                    </div>
                  ))
                )}
              </div>
              {(selected.request_contact_name || selected.request_contact_phone || selected.request_contact_email) && (
                <div>
                  <div style={labelStyle}>Contact</div>
                  {selected.request_contact_name && <div style={{ fontSize: "13px", fontWeight: "600", color: colors.text }}>{selected.request_contact_name}</div>}
                  {selected.request_contact_phone && <div style={{ fontSize: "13px", color: colors.slate }}>{selected.request_contact_phone}</div>}
                  {selected.request_contact_email && <div style={{ fontSize: "13px", color: colors.slate }}>{selected.request_contact_email}</div>}
                </div>
              )}
              <div>
                <label style={labelStyle}>Actions Taken</label>
                <textarea value={editActionsTaken} onChange={e => setEditActionsTaken(e.target.value)} style={{ ...inputStyle, minHeight: "100px", resize: "vertical" }} placeholder="Describe what was done to resolve this request..." />
              </div>
              {selected.completed_at && (
                <div>
                  <div style={labelStyle}>Completed</div>
                  <div style={{ fontSize: "13px", color: colors.green }}>{formatDate(selected.completed_at)}</div>
                </div>
              )}
            </div>
            {canEditDelete && (
              <div style={{ padding: "16px 24px", borderTop: `1px solid ${colors.border}`, display: "flex", gap: "10px" }}>
                {!deleteConfirm ? (
                  <>
                    <button onClick={() => setDeleteConfirm(true)} style={{ padding: "9px 16px", borderRadius: "8px", border: `1px solid ${colors.border}`, background: "white", color: colors.red, cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>Delete</button>
                    <button onClick={handleSave} disabled={saving} style={{ flex: 1, padding: "9px 16px", borderRadius: "8px", border: "none", backgroundColor: colors.darkGrey, color: "white", cursor: "pointer", fontSize: "13px", fontWeight: "600", opacity: saving ? 0.6 : 1 }}>
                      {saving ? "Saving..." : "Save Changes"}
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => setDeleteConfirm(false)} style={{ flex: 1, padding: "9px 16px", borderRadius: "8px", border: `1px solid ${colors.border}`, background: "white", cursor: "pointer", fontSize: "13px" }}>Cancel</button>
                    <button onClick={handleDelete} disabled={deleting} style={{ flex: 1, padding: "9px 16px", borderRadius: "8px", border: "none", backgroundColor: colors.red, color: "white", cursor: "pointer", fontSize: "13px", fontWeight: "600", opacity: deleting ? 0.6 : 1 }}>
                      {deleting ? "Deleting..." : "Confirm Delete"}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Create modal */}
      {showCreate && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ backgroundColor: colors.white, borderRadius: "12px", width: "500px", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
            <div style={{ padding: "20px 24px", borderBottom: `1px solid ${colors.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontWeight: "700", fontSize: "16px", color: colors.text }}>New Service Request</div>
              <button onClick={() => { setShowCreate(false); resetCreate(); }} style={{ background: "none", border: "none", cursor: "pointer", color: colors.slate, padding: "4px", lineHeight: 1 }}><LuX size={18} /></button>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", display: "flex", flexDirection: "column", gap: "16px" }}>
              <div>
                <label style={labelStyle}>Site *</label>
                {createSite ? (
                  <div style={{ padding: "10px 14px", border: `2px solid ${colors.teal}`, borderRadius: "8px", display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#f0fafa" }}>
                    <div>
                      <div style={{ fontWeight: "600", fontSize: "14px" }}>{createSite.site_name}</div>
                      <div style={{ fontSize: "12px", color: colors.slate }}>{createSite.site_address}</div>
                    </div>
                    <button onClick={() => { setCreateSite(null); setSiteSearch(""); }} style={{ background: "none", border: "none", cursor: "pointer", color: colors.slate, fontSize: "16px" }}>✕</button>
                  </div>
                ) : (
                  <div style={{ position: "relative" }}>
                    <input style={inputStyle} placeholder="Search by site name or address..." value={siteSearch} onChange={e => setSiteSearch(e.target.value)} />
                    {siteResults.length > 0 && (
                      <div style={{ position: "absolute", top: "100%", left: 0, right: 0, border: `1px solid ${colors.border}`, borderRadius: "6px", backgroundColor: colors.white, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", zIndex: 10 }}>
                        {siteResults.map(s => (
                          <div key={s.id} onClick={() => { setCreateSite(s); setSiteSearch(""); setSiteResults([]); }} style={{ padding: "10px 14px", cursor: "pointer", borderBottom: `1px solid ${colors.border}`, fontSize: "14px" }}>
                            <div style={{ fontWeight: "600" }}>{s.site_name}</div>
                            <div style={{ fontSize: "12px", color: colors.slate }}>{s.site_address}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label style={labelStyle}>Targets *</label>
                <div style={{ fontSize: "12px", color: colors.slate, marginBottom: "8px" }}>
                  One line per thing that needs doing. The tech marks each one
                  off separately.
                </div>
                {createTargets.map((t, i) => (
                  <div key={i} style={{ display: "flex", gap: "6px", marginBottom: "6px" }}>
                    <span style={{ fontSize: "13px", fontWeight: "700", color: colors.slate, width: "18px", paddingTop: "10px" }}>
                      {i + 1}.
                    </span>
                    <textarea
                      style={{ ...inputStyle, minHeight: "44px", resize: "vertical", flex: 1 }}
                      placeholder="e.g. Replace hose reel on L2 corridor"
                      value={t}
                      onChange={(e) =>
                        setCreateTargets((prev) =>
                          prev.map((x, j) => (j === i ? e.target.value : x)),
                        )
                      }
                    />
                    {createTargets.length > 1 && (
                      <button
                        onClick={() =>
                          setCreateTargets((prev) => prev.filter((_, j) => j !== i))
                        }
                        title="Remove this target"
                        style={{ background: "none", border: "none", cursor: "pointer", color: colors.slate, padding: "4px" }}
                      >
                        <LuX size={15} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={() => setCreateTargets((prev) => [...prev, ""])}
                  style={{ display: "inline-flex", alignItems: "center", gap: "5px", padding: "6px 12px", borderRadius: "6px", border: `1px solid ${colors.border}`, backgroundColor: colors.white, color: colors.slate, fontSize: "12px", fontWeight: "600", cursor: "pointer" }}
                >
                  <LuPlus size={13} /> Add target
                </button>
              </div>
              <div>
                <label style={labelStyle}>Assigned To (optional)</label>
                <select value={createAssignedTo} onChange={e => setCreateAssignedTo(e.target.value)} style={{ ...inputStyle, width: "100%" }}>
                  <option value="">Unassigned</option>
                  {users.map(u => <option key={u.id} value={String(u.id)}>{userName(u)}</option>)}
                </select>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <label style={labelStyle}>Date (optional)</label>
                  <input
                    type="date"
                    value={createScheduledDate}
                    onChange={e => setCreateScheduledDate(e.target.value)}
                    disabled={createFlexible}
                    style={{ ...inputStyle, width: "100%", opacity: createFlexible ? 0.5 : 1 }}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Time (optional)</label>
                  <input
                    type="time"
                    value={createScheduledTime}
                    onChange={e => setCreateScheduledTime(e.target.value)}
                    disabled={createFlexible}
                    style={{ ...inputStyle, width: "100%", opacity: createFlexible ? 0.5 : 1 }}
                  />
                </div>
              </div>

              <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "13px", color: colors.text }}>
                <input
                  type="checkbox"
                  checked={createFlexible}
                  onChange={e => {
                    setCreateFlexible(e.target.checked);
                    // A fixed time and "do anytime" are contradictory; clear
                    // one when the other is chosen.
                    if (e.target.checked) {
                      setCreateScheduledDate("");
                      setCreateScheduledTime("");
                    }
                  }}
                />
                No fixed time — do anytime
              </label>
              <div style={{ fontSize: "12px", color: colors.slate, marginTop: "-4px" }}>
                Left without a date, this goes in the tech's Do Anytime list.
              </div>
              <div>
                <button onClick={() => setShowContact(v => !v)} style={{ background: "none", border: "none", cursor: "pointer", color: colors.blue, fontSize: "13px", fontWeight: "600", padding: 0, display: "flex", alignItems: "center", gap: "4px" }}>
                  <LuChevronDown size={14} style={{ transform: showContact ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
                  Contact details (optional)
                </button>
                {showContact && (
                  <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "10px" }}>
                    <div><label style={labelStyle}>Contact Name</label><input style={inputStyle} value={createContactName} onChange={e => setCreateContactName(e.target.value)} /></div>
                    <div><label style={labelStyle}>Phone</label><input style={inputStyle} value={createContactPhone} onChange={e => setCreateContactPhone(e.target.value)} /></div>
                    <div><label style={labelStyle}>Email</label><input style={inputStyle} value={createContactEmail} onChange={e => setCreateContactEmail(e.target.value)} /></div>
                  </div>
                )}
              </div>
            </div>
            <div style={{ padding: "16px 24px", borderTop: `1px solid ${colors.border}`, display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button onClick={() => { setShowCreate(false); resetCreate(); }} style={{ padding: "9px 18px", borderRadius: "8px", border: `1px solid ${colors.border}`, background: "white", cursor: "pointer", fontSize: "14px" }}>Cancel</button>
              <button onClick={handleCreate} disabled={creating || !createSite || !createTargets.some((t) => t.trim())} style={{ ...styles.addButton, padding: "9px 18px", opacity: creating || !createSite || !createTargets.some((t) => t.trim()) ? 0.6 : 1 }}>
                {creating ? "Creating..." : "Create Request"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
